import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { DsrResource } from '@getaccept/lib-shared/src/library/types/dsr-resource';
import type { DsrDirectory } from '@getaccept/lib-shared/src/library/types/dsr-directory';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import { DealroomQueryKey } from '@getaccept/lib-shared/src/rooms/enums/dealroom-query-key';
import type { DsrResourceListResult } from '@getaccept/lib-shared/src/library/types/dsr-list-result';
import { DealroomStatus } from '@getaccept/lib-shared/src/enums/dsr-status';
import type { ListDealroomsDto } from '../types/list-dealrooms-dto';
import { DsrMutations } from '../graphql/dsr.mutations';
import type { CreateDsrTemplateDTO } from '../types/create-dsr-template-dto';
import { DsrQueries } from '../graphql/dsr.queries';
import type { LoadResourcesDTO } from '../types/load-resources-dto';
import type { CreateDsrDirectoryDTO } from '../types/create-dsr-directory-dto';
import type { CreateDsrEditorResourceDTO } from '../types/create-dsr-editor-resource-dto';
import type { UpdateDsrDirectoryDTO } from '../types/update-dsr-directory-dto';
import type { UpdateDsrResourceDto } from '../types/update-dsr-resource-dto';
import type {
  CreateRoomFromTemplatePayload,
  CreateRoomPayload,
} from '../types/create-room-payload';
import type { RoomList } from '../../../rooms/types/room-list';
import type { RoomActivityMeta } from '../../../rooms/types/rooms-item';

export class DsrService {
  private static clearResourcesCache() {
    apolloClientInternal.cache.evict({
      fieldName: DealroomQueryKey.Resources,
    });
  }

  private static clearDirectoriesCache() {
    apolloClientInternal.cache.evict({
      fieldName: DealroomQueryKey.Directories,
    });
  }

  static async createDealroom(payload: CreateRoomPayload): Promise<string> {
    const {
      data: { createDealroom },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.createDealroom,
      variables: { payload },
    });

    return createDealroom?.id;
  }

  static async createTemplate(payload: CreateDsrTemplateDTO): Promise<string> {
    const {
      data: { createDsrTemplate },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.createTemplate,
      variables: { payload },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return createDsrTemplate.id;
  }

  static async createDirectory(payload: CreateDsrDirectoryDTO): Promise<DsrDirectory> {
    const {
      data: { createDsrDirectory },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.createDirectory,
      variables: { payload },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return createDsrDirectory;
  }

  static async updateDirectory(payload: UpdateDsrDirectoryDTO): Promise<boolean> {
    const {
      data: { updateDsrDirectory },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.updateDirectory,
      variables: { payload },
    });
    return updateDsrDirectory;
  }

  static async createEditorResource(payload: CreateDsrEditorResourceDTO): Promise<DsrResource> {
    const {
      data: { createDsrEditorResource },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.createEditorResource,
      variables: { payload },
    });
    DsrService.clearResourcesCache();
    return createDsrEditorResource;
  }

  static async deleteDirectory(id: string): Promise<boolean> {
    const {
      data: { deleteDsrDirectory },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.deleteDirectory,
      variables: { id },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return !!deleteDsrDirectory;
  }

  static async loadResources(payload: LoadResourcesDTO): Promise<DsrResourceListResult> {
    const {
      data: { dsrResources: dsrResourceListResult },
    } = await apolloClientInternal.query({
      query: DsrQueries.resources,
      variables: { ...payload },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return dsrResourceListResult;
  }

  static async loadFolderTotalCount(
    payload: LoadResourcesDTO
  ): Promise<Omit<DsrResourceListResult, 'resources'>> {
    const {
      data: { dsrResources: dsrResourceListResult },
    } = await apolloClientInternal.query({
      query: DsrQueries.folderTotalCount,
      variables: { ...payload },
      fetchPolicy: GraphQLFetchPolicy.CacheFirst,
    });
    return dsrResourceListResult;
  }

  static async loadResource(id: string): Promise<DsrResource> {
    const {
      data: { dsrResource },
    } = await apolloClientInternal.query({
      query: DsrQueries.loadResource,
      variables: { id },
      fetchPolicy: GraphQLFetchPolicy.CacheFirst,
    });
    return dsrResource;
  }

  static async updateResource(payload: UpdateDsrResourceDto): Promise<boolean> {
    const {
      data: { updateDsrResource },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.updateResource,
      variables: { payload },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return updateDsrResource;
  }

  static async deleteResource(id: string): Promise<boolean> {
    const {
      data: { deleteDsrResource },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.deleteResource,
      variables: { id },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return !!deleteDsrResource;
  }

  static async updateRoom(dealroomId: string, status: DealroomStatus): Promise<boolean> {
    const {
      data: { updateDealroom },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.update,
      variables: { dealroomId, payload: { status } },
    });

    return updateDealroom;
  }

  static async duplicateResource(resourceId: string): Promise<DsrResource> {
    const {
      data: { duplicateDsrResource },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.duplicateResource,
      variables: { resourceId },
    });
    DsrService.clearDirectoriesCache();
    DsrService.clearResourcesCache();
    return duplicateDsrResource;
  }

  static async publish(dealroomId: string): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DsrMutations.publish,
      variables: { dealroomId, message: '' },
    });

    return data;
  }

  static async discardChanges(dealroomId: string): Promise<boolean> {
    const {
      data: { discardDealroomChanges },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.discardChanges,
      variables: { dealroomId },
    });
    return discardDealroomChanges;
  }

  static async loadUpdatedRoomActivity(id: string): Promise<RoomActivityMeta> {
    const {
      data: { getDealroom },
    } = await apolloClientInternal.query({
      query: DsrQueries.loadRoomActivity,
      variables: { id },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return getDealroom;
  }

  static async loadMany({
    statuses,
    userIds = undefined,
    orderBy,
    limit = 20,
    offset,
    searchTerms,
  }: ListDealroomsDto): Promise<RoomList> {
    const query = {
      query: DsrQueries.roomsItems,
      variables: {
        statuses,
        userIds,
        orderBy: [
          {
            direction: orderBy.sortOrder,
            attribute: orderBy.sortBy,
          },
        ],
        limit,
        offset: offset * limit,
        searchTerms,
      },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    };

    const {
      data: { dealrooms },
    } = await apolloClientInternal.query({
      ...query,
      query: statuses.includes(DealroomStatus.Published)
        ? DsrQueries.roomsItemsWithUnreadCount
        : DsrQueries.roomsItems,
    });
    return dealrooms;
  }

  static async useTemplate({
    id,
    name,
    companyName,
  }: CreateRoomFromTemplatePayload): Promise<string> {
    const {
      data: { useDsrTemplate },
    } = await apolloClientInternal.mutate({
      mutation: DsrMutations.useDsrTemplate,
      variables: {
        dsrTemplateId: id,
        name,
        companyName,
      },
    });

    apolloClientInternal.cache.evict({
      fieldName: DealroomQueryKey.Resources,
    });

    return useDsrTemplate.id;
  }

  static async createCollaborator(dealroomId: string, userId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DsrMutations.createParticipant,
      variables: { payload: { userId }, dealroomId },
    });
  }
}
